/**
 * Generates a unique ID.
 * @returns A unique string.
 */
export function generateUniqueId(): string {
  return Math.random().toString(36).substring(2, 9);
}

/**
 * Delays execution for a specific number of milliseconds.
 * @param ms - Milliseconds to wait.
 * @returns A promise that resolves after the specified time.
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Recursively creates a deep clone of the provided object.
 *
 * @param obj - The object to clone. Can be of any type.
 * @param seen - A WeakMap used to track visited objects and avoid infinite recursion in case of circular references.
 * @returns A deep clone of the input object. Primitive values are returned as-is.
 *
 * ### Key Details:
 * - Handles arrays and objects by creating new instances.
 * - Avoids circular reference issues by tracking visited objects using a WeakMap.
 * - Does not clone non-enumerable properties or the prototype chain.
 */
export function deepClone(obj: any, seen = new WeakMap()): any {
  // Return primitive types or null directly
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  // Return the previously cloned reference for circular references
  if (seen.has(obj)) {
    return seen.get(obj);
  }

  // Determine the type of the object (array or object) and create an empty clone
  const clone: any = Array.isArray(obj) ? [] : {};
  // Mark the current object as visited with its clone
  seen.set(obj, clone);

  // Recursively clone properties of the object
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      clone[key] = deepClone(obj[key], seen);
    }
  }

  return clone;
}
